import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Truck, 
    Wrench, 
    Building2,
    Package,
    ArrowLeft,
    Search,
    Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
    available: 'status-available',
    deployed: 'status-info',
    in_use: 'status-info',
    maintenance: 'status-warning',
    out_of_service: 'status-unavailable'
};

const STATUS_LABELS = {
    available: 'Available',
    deployed: 'Deployed',
    in_use: 'In Use',
    maintenance: 'Maintenance',
    out_of_service: 'Out of Service'
};

export default function ResourcesPage() {
    const navigate = useNavigate();
    const { hasUnitAccess } = useAuth();
    const [vehicles, setVehicles] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    
    useEffect(() => {
        fetchData();
    }, []);
    
    const fetchData = async () => {
        try {
            const [vehiclesRes, equipmentRes] = await Promise.all([
                axios.get(`${API_URL}/vehicles`),
                axios.get(`${API_URL}/equipment`)
            ]);
            setVehicles(vehiclesRes.data);
            setEquipment(equipmentRes.data);
        } catch (error) {
            console.error('Failed to fetch resources:', error);
            toast.error('Failed to load resources');
        } finally {
            setLoading(false);
        }
    };
    
    const filteredVehicles = vehicles.filter(v =>
        v.name?.toLowerCase().includes(search.toLowerCase()) ||
        v.registration?.toLowerCase().includes(search.toLowerCase())
    );
    
    const filteredEquipment = equipment.filter(e =>
        e.name?.toLowerCase().includes(search.toLowerCase()) ||
        e.equipment_type?.toLowerCase().includes(search.toLowerCase())
    );
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background p-4">
                <div className="space-y-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="skeleton h-20 rounded-lg"></div>
                    ))}
                </div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background" data-testid="resources-page">
            {/* Header */}
            <header className="bg-card border-b border-border sticky top-0 z-30">
                <div className="flex items-center h-14 px-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/portal')} data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-lg font-semibold ml-2">Fleet, Facilities & Stores</h1>
                </div>
            </header>
            
            {/* Search */}
            <div className="p-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Search resources..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="search-resources"
                    />
                </div>
            </div>
            
            <Tabs defaultValue="vehicles" className="px-4">
                <TabsList className="w-full grid grid-cols-2">
                    <TabsTrigger value="vehicles" data-testid="tab-vehicles">
                        <Truck className="w-4 h-4 mr-2" />
                        Vehicles ({vehicles.length})
                    </TabsTrigger>
                    <TabsTrigger value="equipment" data-testid="tab-equipment">
                        <Wrench className="w-4 h-4 mr-2" />
                        Equipment ({equipment.length})
                    </TabsTrigger>
                </TabsList>
                
                <TabsContent value="vehicles" className="mt-4 space-y-3 pb-20">
                    {filteredVehicles.length > 0 ? (
                        filteredVehicles.map((vehicle) => (
                            <Card key={vehicle.id} className="hover-lift" onClick={() => hasUnitAccess() && navigate('/vehicles')}>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-primary/10">
                                                <Truck className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium">{vehicle.name}</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {vehicle.registration} • {vehicle.vehicle_type}
                                                </p>
                                            </div>
                                        </div>
                                        <Badge className={`status-badge ${STATUS_COLORS[vehicle.status]}`}>
                                            {STATUS_LABELS[vehicle.status]}
                                        </Badge>
                                    </div>
                                    {vehicle.current_location && (
                                        <p className="text-sm text-muted-foreground mt-2">
                                            📍 {vehicle.current_location}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        <Card>
                            <CardContent className="py-8 text-center text-muted-foreground">
                                <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No vehicles found</p>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>
                
                <TabsContent value="equipment" className="mt-4 space-y-3 pb-20">
                    {filteredEquipment.length > 0 ? (
                        filteredEquipment.map((item) => (
                            <Card key={item.id} className="hover-lift" onClick={() => hasUnitAccess() && navigate('/equipment')}>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-primary/10">
                                                <Wrench className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium">{item.name}</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {item.equipment_type}
                                                    {item.serial_number && ` • ${item.serial_number}`}
                                                </p>
                                            </div>
                                        </div>
                                        <Badge className={`status-badge ${STATUS_COLORS[item.status]}`}>
                                            {STATUS_LABELS[item.status]}
                                        </Badge>
                                    </div>
                                    {item.location && (
                                        <p className="text-sm text-muted-foreground mt-2">
                                            📍 {item.location}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        <Card>
                            <CardContent className="py-8 text-center text-muted-foreground">
                                <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No equipment found</p>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
